import { supabase } from '../../lib/supabase';
import type { User } from '../../contexts/auth.types';
import type { Session } from '@supabase/supabase-js';
import type { RpcProfileWithRoles } from './types';
import { resolveActiveSide, roleContextForSide } from '@trade-connector/shared';

export function resolveCompanyContextFromRpc(profile: RpcProfileWithRoles): {
  accountType: User['account_type'];
  companyId: string | null;
  accountMode?: 'single' | 'dual';
  primaryAccountType?: 'customer' | 'trade_company';
  activeRole?: 'customer' | 'trade_company';
} {
  const isPlatformAdmin = !!profile.is_platform_admin;
  if (isPlatformAdmin) {
    return { accountType: 'platform', companyId: null };
  }

  // The app and the session (sync_user_session) pick a dual account's side by the same rule.
  const active = resolveActiveSide(profile);
  if (active.isDual && active.side) {
    return {
      accountType: 'dual',
      companyId: active.companyId,
      accountMode: 'dual',
      primaryAccountType: profile.primary_account_type ?? 'customer',
      activeRole: roleContextForSide(active.side),
    };
  }

  if (profile.customer_id) {
    return {
      accountType: 'customer',
      companyId: profile.customer_id,
      accountMode: 'single',
      activeRole: 'customer',
    };
  }

  if (profile.trade_company_id) {
    return {
      accountType: 'trade_company',
      companyId: profile.trade_company_id,
      accountMode: 'single',
      activeRole: 'trade_company',
    };
  }

  return { accountType: 'platform', companyId: null };
}

export async function fetchTradeCompanyStatus(companyId: string | null): Promise<User['trade_company_status']> {
  if (!companyId) return null;
  try {
    const { data, error } = await supabase
      .from('trade_companies')
      .select('status')
      .eq('id', companyId)
      .single();
    if (error || !data) return null;
    return data.status as User['trade_company_status'];
  } catch (e) {
    console.error('Error fetching trade company status', e);
    return null;
  }
}

export async function fetchCustomerStatus(customerId: string | null): Promise<User['customer_status']> {
  if (!customerId) return null;
  try {
    const { data, error } = await supabase
      .from('customers')
      .select('status')
      .eq('id', customerId)
      .single();
    if (error || !data) return null;
    return data.status as User['customer_status'];
  } catch (e) {
    console.error('Error fetching customer status', e);
    return null;
  }
}

export async function fetchDirectJobSettings(companyId: string | null): Promise<boolean> {
  if (!companyId) return false;
  try {
    const { data, error } = await supabase
      .from('direct_job_settings')
      .select('enabled')
      .eq('trade_company_id', companyId)
      .maybeSingle();

    if (error) {
      if (error.code !== 'PGRST116') {
        console.warn('Error fetching direct job settings', error);
      }
      return false;
    }
    return data?.enabled ?? false;
  } catch (e) {
    console.warn('Exception fetching direct job settings', e);
    return false;
  }
}

export function createFallbackUser(sessionUser: Session['user']): User {
  return {
    id: sessionUser.id,
    email: sessionUser.email || '',
    first_name: sessionUser.user_metadata?.first_name || '',
    last_name: sessionUser.user_metadata?.last_name || '',
    name: sessionUser.email?.split('@')[0] || 'User',
    roles: [{ role_name: 'USER', role_description: 'Basic User' }],
    permissions: ['user:view'],
    account_type: 'platform',
    company_id: null,
    customer_id: null,
    trade_company_id: null,
    is_platform_admin: false,
    is_fallback: true,
    customer_status: null,
    trade_company_status: null,
    direct_jobs_enabled: false,
  };
}

export function createBasicUser(sessionUser: Session['user']): User {
  return {
    id: sessionUser.id,
    email: sessionUser.email || '',
    first_name: '',
    last_name: '',
    name: sessionUser.email?.split('@')[0] || 'User',
    roles: [{ role_name: 'USER', role_description: 'Basic User' }],
    permissions: ['user:view'],
    account_type: 'platform',
    company_id: null,
    customer_id: null,
    trade_company_id: null,
    is_platform_admin: false,
    is_fallback: true,
    customer_status: null,
    trade_company_status: null,
    direct_jobs_enabled: false,
  };
}

/**
 * A client session built before the client capability migration (20261031100000) reached the database holds no
 * `client:*` keys. Web and backend deploy in parallel, so rebuild such a session on the next token refresh or tab
 * focus instead of keeping it until a hard reload (release review 3815 C1). Platform admins and the trade side of a
 * dual account are not client sessions.
 */
export function isClientSessionMissingClientKeys(user: User): boolean {
  if (user.is_platform_admin || !user.customer_id) return false;
  if (user.account_type === 'dual' && user.active_role !== 'customer') return false;
  return !user.permissions.some((key) => key.startsWith('client:'));
}
